import { useContext, type ReactNode } from "react";
import { X } from "lucide-react";
import {
  AnswerTile,
  Banner,
  Button,
  ErrorState,
  LoadingState,
  StatusDot,
  TableEmpty,
  TablePagination,
} from "@/shared/rd";
import { useLocale, useTranslations } from "@/lib/i18n/intl";
import { keys } from "@/lib/i18n/portal";
import type { WhatsappCoverage } from "../types/whatsapp.types";
import { whatsappFailureKey } from "../services/useWhatsapp";
import { statusTone } from "./status-tone";
import { WhatsappFilterContext } from "./filter-context";
const copy = keys.whatsapp;

/**
 * A read's first load and failure, drawn with the kit's system states. A
 * failed read never renders as an empty list; the failure's own reason
 * (permissions, configuration, rate limit…) is the error's second line.
 */
export function QueryState({
  loading,
  error,
  retry,
  page,
  rows = 0,
  children,
}: {
  loading: boolean;
  error: unknown;
  retry: () => unknown;
  /** What is loading, lower-case where the language has case ("templates"). */
  page: string;
  /** Skeleton rows under the loading card; 0 inside a drawer or card. */
  rows?: number;
  children: ReactNode;
}) {
  const t = useTranslations();
  if (loading)
    return (
      <LoadingState
        title={t(keys.shell.loadingTitle, { page })}
        sub={t(keys.shell.loadingSub)}
        rows={rows}
      />
    );
  if (error)
    return (
      <ErrorState
        title={t(keys.shell.errorTitle, { page })}
        sub={t(whatsappFailureKey(error))}
        onRetry={() => void retry()}
        retryLabel={t(keys.shell.retry)}
      />
    );
  return <>{children}</>;
}

/**
 * A failed WhatsApp write, at the top of the form, row or panel it came from:
 * what failed (title) and why (the failure's own sentence, which already
 * says the inputs are kept). A plain failure offers Try again; an uncertain
 * one (Meta may have applied it) never offers a blind retry — only "I checked
 * Meta", which re-enables submitting.
 */
export function WriteFailure({
  title,
  message,
  uncertain,
  onTryAgain,
  onCheckedOutcome,
  retrying = false,
}: {
  title: string;
  message: string;
  uncertain: boolean;
  onTryAgain?: () => void;
  onCheckedOutcome: () => void;
  retrying?: boolean;
}) {
  const t = useTranslations();
  return (
    <Banner
      tone="error"
      title={title}
      actions={
        uncertain ? (
          <Button variant="secondary" size="sm" onClick={onCheckedOutcome}>
            {t(copy.checkOutcome)}
          </Button>
        ) : onTryAgain ? (
          <Button size="sm" loading={retrying} loadingLabel={t(keys.shell.retrying)} onClick={onTryAgain}>
            {t(keys.shell.retry)}
          </Button>
        ) : undefined
      }
    >
      {message}
    </Banner>
  );
}

/**
 * A table with no rows. With filters on, it says nothing matched and offers
 * "Clear filters"; otherwise it is the first-use board: what the table holds,
 * where its rows come from, and the create action when there is one.
 */
export function NoRecords({
  title,
  text,
  action,
  matchTitle,
  forceMatch,
  period,
}: {
  /** First use: "No templates yet". */
  title: string;
  /** First use: where the rows come from. */
  text: string;
  /** First use: the create action, if the table has one. */
  action?: ReactNode;
  /** No results: overrides "No records match these filters." */
  matchTitle?: string;
  /** Treat as no-results even without page filters (e.g. data is being recorded). */
  forceMatch?: boolean;
  /**
   * Date-ranged lists: with only the dates narrowing, say "nothing in this
   * period" and offer the widest range instead of "Clear filters".
   */
  period?: { title: string; text: string };
}) {
  const t = useTranslations();
  const { filtered, clear, narrowed = filtered, widenPeriod } = useContext(WhatsappFilterContext);
  if (period && !narrowed && widenPeriod)
    return (
      <TableEmpty
        illustration="admin-no-results"
        title={period.title}
        actions={
          <Button variant="secondary" size="sm" onClick={widenPeriod}>
            {t("whatsapp.showLast90Days")}
          </Button>
        }
      >
        {period.text}
      </TableEmpty>
    );
  if (period && !narrowed && !forceMatch)
    return (
      <TableEmpty illustration="admin-empty-table" title={title} actions={action}>
        {text}
      </TableEmpty>
    );
  if (filtered || forceMatch)
    return (
      <TableEmpty
        illustration="admin-no-results"
        title={matchTitle ?? t(copy.empty)}
        actions={
          filtered && clear ? (
            <Button variant="secondary" size="sm" icon={X} onClick={clear}>
              {t(keys.common.clearFilters)}
            </Button>
          ) : undefined
        }
      >
        {t("whatsapp.emptyFiltersSub")}
      </TableEmpty>
    );
  return (
    <TableEmpty illustration="admin-empty-table" title={title} actions={action}>
      {text}
    </TableEmpty>
  );
}

export function StatusBadge({ value }: { value: string }) {
  const t = useTranslations();
  const label = (copy.states as Record<string, string>)[value];
  return <StatusDot tone={statusTone(value)}>{label ? t(label) : value}</StatusDot>;
}

export function CoverageNote({ coverage }: { coverage: WhatsappCoverage }) {
  const t = useTranslations();
  return (
    <p style={{ margin: 0, fontSize: 13, color: "var(--rd-text-tertiary)" }}>
      {coverage.recordedSince
        ? t(copy.coverage, {
            since: new Date(coverage.recordedSince).toISOString().slice(0, 10),
            days: coverage.retentionDays,
          })
        : t(copy.emptyRecorded)}
    </p>
  );
}

/** The table card's footer: the record count and the page numbers. */
export function Pager({
  page,
  limit,
  total,
  onChange,
}: {
  page: number;
  limit: number;
  total: number;
  onChange: (page: number) => void;
}) {
  const t = useTranslations();
  const locale = useLocale();
  return (
    <TablePagination
      total={total}
      page={page}
      pageSize={limit}
      pageSizes={null}
      onPage={onChange}
      locale={locale}
      labels={{
        range: () => t(copy.totalRows, { total: total.toLocaleString(locale) }),
        rows: "",
        previous: t(copy.previous),
        next: t(copy.next),
        pages: t(copy.pagination),
        rowsPerPage: "",
      }}
    />
  );
}

export function Timestamp({ value }: { value: string | null }) {
  const locale = useLocale();
  return (
    <bdi>
      {value
        ? new Date(value).toLocaleString(locale, { timeZone: "UTC" })
        : "—"}
    </bdi>
  );
}

/** One figure as an answer tile; the number carries `rd-num`. */
export function Metric({
  label,
  value,
  badge,
  sub,
}: {
  label: string;
  value: ReactNode;
  badge?: string;
  sub?: ReactNode;
}) {
  return (
    <AnswerTile
      badge={badge}
      label={label}
      answer={<span className="rd-num" style={{ whiteSpace: "normal", overflowWrap: "anywhere" }}>{value}</span>}
      sub={sub}
    />
  );
}

/** A card with the 15/600 title and a one-line note. */
export function Section({
  title,
  note,
  actions,
  children,
}: {
  title: ReactNode;
  note?: ReactNode;
  actions?: ReactNode;
  children: ReactNode;
}) {
  return (
    <section className="rd-card" style={{ padding: "16px 20px", display: "flex", flexDirection: "column", gap: 12, minWidth: 0 }}>
      <div style={{ display: "flex", alignItems: "flex-start", gap: 12 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <h2 style={{ margin: 0, fontSize: 15, fontWeight: 600 }}>{title}</h2>
          {note ? (
            <p style={{ margin: "2px 0 0", fontSize: 13, color: "var(--rd-text-secondary)" }}>{note}</p>
          ) : null}
        </div>
        {actions ?? null}
      </div>
      {children}
    </section>
  );
}

